import React, { useRef, useState, useMemo, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import LinearProgressCountUp from "../../../../components/LinearProgressBar";
import TaskTabsModal from "../../../../components/Modal/TaskTabsModal";
import { TaskIcon } from "../../../../components/Icon";
import colors from "../../../../assets/theme/light/colors";
import Tooltip from "../../../../components/Tooltip";
import { useTranslation } from "react-i18next";
import { formatDateTimeToString } from "../../../../UTC2Local";
import IconBadge from "../../../../components/StatusCell/IconBadge";

const RowsSubrows = ({
	row,
	column,
	rowGuid,
	handleRefetchTree,
	data,
	updateData,
	getTotalFilters,
}) => {
	const [openModal, setOpenModal] = useState(false);
	const [projectID, setProjectID] = useState(null);
	const [taskID, setTaskID] = useState(null);
	const [isHovered, setIsHovered] = useState(false);
	const type = useRef("task");
	const { t } = useTranslation();

	// 1) Destructure once, fall back to empty object
	const task = row?.task ?? {};
	const {
		guid,
		taskGuid,
		id,
		title,
		assignedTo,
		area,
		parentName,
		ticketName,
		projectName,
		priority,
		percentageComplete,
		status,
		billable,
		estimatedStartDate,
		estimatedEndDate,
		estimatedTime,
		actualStartDate,
		actualEndDate,
		actualTime,
		actualBillableTime,
		actualNonBillableTime,
		createdBy,
		modifiedBy,
		createdAt,
		modifiedAt,
		externalReference,
		customerReferenceProject,
		parentTaskGuid,
		level = 0,
		tasks = [],
	} = task;

	// 2) Memoize all derived/ formatted values
	const formatted = useMemo(
		() => ({
			estimatedStart: formatDateTimeToString(estimatedStartDate, false),
			estimatedEnd: formatDateTimeToString(estimatedEndDate, false),
			actualStart: formatDateTimeToString(actualStartDate, false),
			actualEnd: formatDateTimeToString(actualEndDate, false),
			createdAt: formatDateTimeToString(createdAt, false),
			modifiedAt: formatDateTimeToString(modifiedAt, false),
			estTime:
				estimatedTime != null ? parseFloat(estimatedTime).toFixed(2) : null,
			actTime: actualTime != null ? parseFloat(actualTime).toFixed(2) : null,
			billHrs:
				actualBillableTime != null
					? parseFloat(actualBillableTime).toFixed(2)
					: "0.00",
			nonBillHrs:
				actualNonBillableTime != null
					? parseFloat(actualNonBillableTime).toFixed(2)
					: "0.00",
			createdByName:
				createdBy?.firstName || createdBy?.lastName
					? `${createdBy.firstName || ""} ${
							createdBy.lastName || ""
					  }`.trim()
					: null,
			modifiedByName:
				modifiedBy?.firstName || modifiedBy?.lastName
					? `${modifiedBy.firstName || ""} ${
							modifiedBy.lastName || ""
					  }`.trim()
					: null,
		}),
		[
			estimatedStartDate,
			estimatedEndDate,
			actualStartDate,
			actualEndDate,
			createdAt,
			modifiedAt,
			estimatedTime,
			actualTime,
			actualBillableTime,
			actualNonBillableTime,
			createdBy,
			modifiedBy,
		]
	);

	// 3) Modal opener
	const handleTitleClick = useCallback(() => {
		setTaskID(guid || taskGuid);
		setProjectID(task.projectId);
		setOpenModal(true);
	}, [guid, task.projectId]);
	// 4) Build a lookup for each column.id
	const cellValue = useMemo(() => {
		const commonTitle = (
			<Box
				onClick={handleTitleClick}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				sx={{
					display: "flex",
					alignItems: "center",
					ml: level * 2,
					cursor: "pointer",
					color: isHovered ? "#1086ef" : "inherit",
					textDecoration: isHovered ? "underline" : "none",
				}}
			>
				<Box sx={{ mr: 0.5 }}>
					{parentTaskGuid == null ? (
						<Tooltip title={t("backlog.tooltip.task")}>
							<TaskIcon />
						</Tooltip>
					) : (
						<Tooltip title={t("backlog.tooltip.subtask")}>
							<Box
								component="svg"
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 24 24"
								sx={{
									width: 20,
									height: 20,
									stroke: colors.light_logo_blue,
									marginLeft: "15px",
								}}
							>
								<path
									d="M15 11l4 4l-4 4m4 -4h-11a4 4 0 0 1 0 -8h1"
									fill="none"
									strokeWidth={1}
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</Box>
						</Tooltip>
					)}
				</Box>
				<Tooltip title={title}>
					<Typography
						sx={{
							fontFamily:
								"Roboto, Helvetica, Arial, sans-serif !important",
							fontSize: 14,
							fontWeight: "400",
						}}
						noWrap
					>
						{title}
					</Typography>
				</Tooltip>
			</Box>
		);

		return (
			{
				Title: commonTitle,
				ID: id,
				Employee: assignedTo,
				Area: area?.description ?? area,
				Parent: parentName,
				Ticket: ticketName,
				Project: <Tooltip title={projectName}>{projectName}</Tooltip>,
				ExternalReference: externalReference,
				CustomerReferenceProject: customerReferenceProject,
				BillableHours: formatted.billHrs,
				NonBillableHours: formatted.nonBillHrs,
				Progress: <LinearProgressCountUp progress={percentageComplete} />,
				EstimatedStartDate: formatted.estimatedStart,
				EstimatedEndDate: formatted.estimatedEnd,
				EstimatedTime: formatted.estTime,
				ActualStartDate: formatted.actualStart,
				ActualEndDate: formatted.actualEnd,
				ActualTime: formatted.actTime,
				CreatedBy: formatted.createdByName,
				CreationDate: formatted.createdAt,
				ModifiedBy: formatted.modifiedByName,
				ModificationDate: formatted.modifiedAt,
				Billable: <IconBadge success={billable} />,
			}[column.id] ?? null
		);
	}, [
		column.id,
		handleTitleClick,
		isHovered,
		level,
		parentTaskGuid,
		id,
		assignedTo,
		area,
		parentName,
		ticketName,
		projectName,
		externalReference,
		customerReferenceProject,
		percentageComplete,
		formatted,
		billable,
		title,
		t,
	]);

	return (
		<>
			{cellValue}

			{openModal && (
				<TaskTabsModal
					showModal={openModal}
					setShowModal={setOpenModal}
					loading={false}
					projectID={projectID}
					projectName={projectName}
					taskID={taskID}
					handleChangeTask={setTaskID}
					handleChangeProject={setProjectID}
					taskName={title}
					refetch={handleRefetchTree}
					fromProject
					open={JSON.parse(
						sessionStorage.getItem("unsavedChanges") || "false"
					)}
					modifyFormFlag={() =>
						sessionStorage.setItem(
							"unsavedChanges",
							JSON.stringify(false)
						)
					}
					setType={(v) => (type.current = v)}
					type={type.current}
					data={data}
					updateData={updateData}
					getTotalFilters={getTotalFilters}
					addNewSubtask
				/>
			)}
		</>
	);
};

export default RowsSubrows;
